import { ExamLocationRequestWorkflowStepCode, ExamLocationVerdict } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  Alert,
  Box,
  Button,
  Divider,
  EditOutlinedIcon,
  EventAvailableOutlinedIcon,
  Flex,
  PaperSection,
  SectionTitle,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission, validatePermissions } from '@shared/permissions';
import { dayjs, formatDateTime } from '@shared/utils';
import { useMemo } from 'react';

import { VerdictResultLabel } from '../../components';
import ExamLocationManagementRoomDetailsEdit from '../../components/exam-location-management-room-details-edit/exam-location-management-room-details-edit';
import { useExamRoomRequestInfo } from '../../providers';
import { ExamRoomCreateModal } from '../exam-room-create-modal';

const LOCATION_DETAILS = [
  {
    label: 'จังหวัด',
    value: [['room', 'location', 'province']],
  },
  {
    label: 'สถานที่/หน่วยงาน (ภาษาไทย)',
    value: [['room', 'location', 'nameTh']],
  },
  {
    label: 'สถานที่/หน่วยงาน (ภาษาอังกฤษ)',
    value: [['room', 'location', 'nameEn']],
  },
  {
    label: 'อาคาร',
    value: [['room', 'location', 'building']],
  },
];

const ROOM_INFORMATION = [
  {
    label: 'ชั้น',
    value: [['room', 'floor']],
  },
  {
    label: 'ชื่อห้อง',
    value: [['room', 'name']],
  },
  {
    label: 'เลขห้อง',
    value: [['room', 'roomNumber']],
  },
  {
    label: 'จำนวนที่นั่ง',
    value: [['room', 'seatCount']],
  },
];

const getNestedValue = (obj: Record<string, unknown>, path: string | string[]): unknown => {
  const pathArray = Array.isArray(path) ? path : [path];
  return pathArray.reduce((current: unknown, key: string) => {
    return (current as Record<string, unknown>)?.[key];
  }, obj);
};

export default function ExamLocationManagementRoomRequest({
  examCenterCode,
  requestId,
  roomRequestId,
}: {
  examCenterCode: string;
  requestId: string;
  roomRequestId: string;
}) {
  const [isOpen, examRoomCreateModalHandlers] = useDisclosure(false);
  const { open: openExamRoomCreateModal, close: closeExamRoomCreateModal } =
    examRoomCreateModalHandlers;

  const { examRoomRequestInfo, isReadonly: isDraft } = useExamRoomRequestInfo();

  const { permissions } = usePermission();
  const hasUpdatePermission = validatePermissions(
    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
    permissions,
  );

  const isReadonly = isDraft || !hasUpdatePermission;

  const examRoomRequestStatus: 'draft' | 'in-review' | 'rejected' = examRoomRequestInfo.step
    ? examRoomRequestInfo.step === ExamLocationRequestWorkflowStepCode.ExamLocationRequestRejected
      ? 'rejected'
      : 'in-review'
    : 'draft';

  const shouldShowVerdict =
    examRoomRequestInfo.step === ExamLocationRequestWorkflowStepCode.ExamLocationRequestRejected;

  const lastRejectedVerdict = useMemo(() => {
    const verdicts = examRoomRequestInfo.examLocationRequestVerdicts || [];
    if (verdicts.length === 0 || !shouldShowVerdict) {
      return null;
    }

    let lastRejectedVerdict = null;

    for (const verdict of verdicts) {
      if (verdict.verdict !== ExamLocationVerdict.Rejected) continue;
      if (
        !lastRejectedVerdict ||
        dayjs(verdict.requestedAt).isAfter(dayjs(lastRejectedVerdict.requestedAt))
      ) {
        lastRejectedVerdict = verdict;
      }
    }

    return lastRejectedVerdict;
  }, [examRoomRequestInfo, shouldShowVerdict]);

  const roomRequestData = {
    room: {
      name: examRoomRequestInfo.name,
      building:
        examRoomRequestInfo.examLocationRequestInfo.buildingName ??
        examRoomRequestInfo.examLocationRequestInfo.building?.nameTh ??
        '-',
      floor: examRoomRequestInfo.floor,
      roomNumber: examRoomRequestInfo.roomNumber || '-',
      seatCount: examRoomRequestInfo.seatCount,
      location: {
        province: examRoomRequestInfo.examLocationRequestInfo.province.nameTh,
        nameTh: examRoomRequestInfo.examLocationRequestInfo.nameTh,
        nameEn: examRoomRequestInfo.examLocationRequestInfo.nameEn || '-',
        building:
          examRoomRequestInfo.examLocationRequestInfo.building?.nameTh ??
          examRoomRequestInfo.examLocationRequestInfo.buildingName ??
          '-',
      },
    },
  };

  return (
    <>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          p: theme.spacing(6),
        })}
      >
        <PaperSection title='ข้อมูลสถานที่สอบ'>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(6),
              p: theme.spacing(6),
            })}
          >
            <SectionTitle title='Location Details' />

            {LOCATION_DETAILS.map((detail) => (
              <Flex
                key={detail.label}
                sx={(theme) => ({
                  gap: theme.spacing(6),
                })}
              >
                <Text
                  variant='body1Semibold'
                  sx={{
                    maxWidth: '240px',
                    width: '50%',
                  }}
                >
                  {detail.label}
                </Text>

                <Flex
                  sx={(theme) => ({
                    flexDirection: 'column',
                    gap: theme.spacing(4),
                  })}
                >
                  {detail.value.map((accessor, index) => (
                    <Text key={index}>
                      {String(getNestedValue(roomRequestData, accessor) || '')}
                    </Text>
                  ))}
                </Flex>
              </Flex>
            ))}
          </Flex>
        </PaperSection>

        {/* TODO: UI Update */}
        <PaperSection
          title='ข้อมูลห้องสอบ'
          endAdornment={
            !isReadonly ? (
              <Button
                variant='outlined'
                size='small'
                color='secondary'
                sx={{
                  minWidth: 'max-content',
                }}
                startIcon={<EditOutlinedIcon />}
                onClick={openExamRoomCreateModal}
              >
                แก้ไขข้อมูล
              </Button>
            ) : null
          }
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(6),
              p: theme.spacing(6),
            })}
          >
            <SectionTitle title='Room Information' />

            {ROOM_INFORMATION.map((information) => (
              <Flex
                key={information.label}
                sx={(theme) => ({
                  gap: theme.spacing(6),
                })}
              >
                <Text
                  variant='body1Semibold'
                  sx={{
                    maxWidth: '240px',
                    width: '50%',
                  }}
                >
                  {information.label}
                </Text>
                <Flex
                  sx={(theme) => ({
                    flexDirection: 'column',
                    gap: theme.spacing(4),
                  })}
                >
                  {information.value.map((accessor, index) => (
                    <Text key={index}>
                      {String(getNestedValue(roomRequestData, accessor) || '')}
                    </Text>
                  ))}
                </Flex>
              </Flex>
            ))}

            <Divider />

            <SectionTitle title='Room Details' />

            {/* TODO: Check if both forms are completed */}
            {!isReadonly ? (
              <Alert severity='info' title='ข้อมูลห้องสอบ'>
                <Text>กรุณากรอกรายละเอียดข้อมูลห้องสอบให้ครบถ้วน</Text>
              </Alert>
            ) : null}

            <ExamLocationManagementRoomDetailsEdit
              isReadonly={isReadonly}
              isNew={!examRoomRequestInfo?.examRoomRequestPhysicalInfo?.id}
              examCenterCode={examCenterCode}
              examLocationRequestId={requestId}
              examRoomDetailType='physical-info'
              examRoomRequestId={roomRequestId}
              examLocationRequestStatus={examRoomRequestStatus}
            />
            <ExamLocationManagementRoomDetailsEdit
              isReadonly={isReadonly}
              isNew={!examRoomRequestInfo?.examRoomRequestComputerInfo?.id}
              examCenterCode={examCenterCode}
              examLocationRequestId={requestId}
              examRoomDetailType='computer-info'
              examRoomRequestId={roomRequestId}
              examLocationRequestStatus={examRoomRequestStatus}
            />
          </Flex>
        </PaperSection>
        {shouldShowVerdict && lastRejectedVerdict ? (
          <PaperSection title='ผลการอนุมัติ'>
            <Box
              sx={(theme) => ({
                p: theme.spacing(6),
              })}
            >
              <Box
                sx={(theme) => ({
                  p: theme.spacing(4),
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  borderRadius: theme.shape.rounded(2),
                })}
              >
                <Flex
                  sx={(theme) => ({
                    borderLeft: '4px solid',
                    pl: theme.spacing(4),
                    borderColor: theme.palette.error.main,
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  })}
                >
                  <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
                    <Text variant='body2' color='text.secondary'>
                      ความคิดเห็นเพิ่มเติม: {lastRejectedVerdict.comment}
                    </Text>
                    <Flex sx={{ gap: (theme) => theme.spacing(1), alignItems: 'center' }}>
                      <EventAvailableOutlinedIcon
                        sx={{ color: (theme) => theme.palette.text.secondary, fontSize: 16 }}
                      />
                      <Text variant='body2' color='text.secondary'>
                        วันที่ไม่อนุมัติ: {formatDateTime(lastRejectedVerdict.submittedAt)}
                      </Text>
                    </Flex>
                  </Flex>
                  {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                  <VerdictResultLabel variant={lastRejectedVerdict.verdict!} />
                </Flex>
              </Box>
            </Box>
          </PaperSection>
        ) : null}
      </Flex>
      <ExamRoomCreateModal
        open={isOpen}
        onClose={closeExamRoomCreateModal}
        roomRequestId={roomRequestId}
        locationRequestId={requestId}
        defaultValues={{
          name: examRoomRequestInfo.name,
          floor: examRoomRequestInfo.floor,
          roomNumber: examRoomRequestInfo.roomNumber ?? '',
          seatCount: examRoomRequestInfo.seatCount,
        }}
      />
    </>
  );
}
